import React, {useContext, useEffect} from 'react';
import { Theme } from '@mui/material';
import { useDispatch} from 'react-redux';
import LockOpen  from '@mui/icons-material/LockOpen';
import { buttonStyles } from '../../config/Theme';
import { userLogin } from '../../actions/common/AuthenticatedUserActions';
import {makeStyles} from "@mui/styles";
import {AuthenticatedUserContext, LanguageContext} from "../../config/ContextConfig";
import {useLocation} from "react-router";
import ResponsiveButtonWithTooltip from "../common/buttons/ResponsiveButtonWithTooltip";
import TextInput from "../form/TextInput";
import MarkdownRenderer from "../common/markdown/MarkdownRenderer";
import {AppRegistration} from "@mui/icons-material";
import LinkButton from "../common/buttons/LinkButton";
import {RouteConfig} from "../../config/RouteConfig";
import {VisibilityHandler} from "../common/VisibilityHandler";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {sendUserToPage} from "../../actions/common/UserViewSettingsActions";




interface LoginFormProps {

}

/**
 * Generates a set of reusable CSS-in-JS styles for UI components.
 *
 * This function provides a collection of custom styling rules for creating consistent and visually cohesive user interfaces
 * across the application. It adapts styles based on the provided theme, allowing the design to align with the application's
 * branding and accessibility guidelines.
 *
 * Key areas styled include container layouts, form elements, and button properties.
 *
 * @param theme - The application's theme object, providing access to palette, typography, breakpoints, and other styling utilities.
 * @return The generated styling rules to be used with Material-UI components.
 *
 * @throws Will throw an error if the theme object is undefined or lacks required properties.
 */
const useStyles = makeStyles(( theme: Theme ) => ({
    ...buttonStyles(theme),
    root: { },
    grow: { flexGrow: 1, },
    formDiv: {
        marginTop: 12+"rem",
        flexDirection: "row",
        gap: 2+"rem",
        justifyContent: "center",
        flexWrap: "wrap",
    },
    loginDiv: {
        borderRadius: 8,
        border: "2px solid "+ theme.palette.primary.main,
        padding:3+"rem",
        width: 250+"px",
        minWidth: 250+"px",
    },
    form: {
        height: '4.5em',
    },
    textField: {
        width: 100+"%",
        backgroundColor: theme.palette.common.white
    },
    marketingText: {
        fontSize: "1.2rem",
    },
    marketingDiv: {
        borderRadius: 8,
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.secondary.dark
            : theme.palette.secondary.light,
        border: "2px solid "+ theme.palette.secondary.dark,
        padding:3+"rem",
        width: 250+"px",
        minWidth: 250+"px",
    }
}));

/**
 * A React component representing a login form with email and password inputs.
 * It facilitates user authentication by capturing and dispatching login credentials.
 *
 * This form ensures that users provide valid input for email and password and handles
 * login submission through either a button click or pressing the "Enter" key.
 *
 * @param props - The properties used to customize and render the login form component.
 *                It includes any props required for styling, language translation, etc.
 * @return A JSX.Element representing the login form layout, including input fields
 *         and a submit button.
 * @throws Throws an error if the required dependencies (e.g., LanguageContext, dispatch)
 *         are not properly provided or initialized during the component's usage.
 */
function LoginForm( props: LoginFormProps ) {

    const location = useLocation();
    const language = useContext( LanguageContext );
    const classes = useStyles();
    const dispatch = useDispatch();
    const authUser = useContext( AuthenticatedUserContext );



    useEffect(() => {
        if( ( authUser?.id?.rawValue ?? 0 ) > 0 )
            if( location.pathname !== "/login" )
                dispatch( sendUserToPage( location.pathname , "" ) );
            else
                dispatch( sendUserToPage( RouteConfig.dashboard.routes.main.path , "" ) );

        }, [authUser]);




        let mailInput = React.createRef<HTMLInputElement>();
    let passwordInput = React.createRef<HTMLInputElement>();

    const sendForm = () => {

        if( mailInput.current !== null && passwordInput.current !== null ) {

            dispatch(
                userLogin(
                    mailInput.current.value,
                    passwordInput.current.value,
                    location.pathname === "/login"
                        ? "/"
                        : location.pathname
                )
            );
        }
    };

    const sendFormFromEnter = ( e: React.KeyboardEvent<HTMLDivElement> ) => {
        if( e.key === 'Enter' ) {
            sendForm();
        }
    };



    return (

        <div className={ "flex " + classes.formDiv }
             style={{ height: "100%" }}
             onKeyUp={ sendFormFromEnter}
        >
            <div className={ classes.loginDiv }>
                <div className={classes.form}>
                    <TextInput
                        name="mail"
                        type="text"
                        label={language.userManagment.mail}
                        required
                        inputRef={mailInput}
                    />
                </div>
                <div className={classes.form}>
                    <TextInput
                        name="password"
                        type="password"
                        label={language.userManagment.password}
                        required
                        inputRef={passwordInput}
                    />
                </div>
                <div className={classes.form}>
                    <div className="flex justify-center full-width" >
                        <ResponsiveButtonWithTooltip
                            variant={"secondary"}
                            text={language.userManagment.login}
                            icon={<LockOpen/>}
                            style={{ marginTop: "2rem", padding: "1.5rem", fontSize: "1rem"  }}
                            onClick={e => {
                                e.stopPropagation();
                                sendForm();
                            }}
                        />
                    </div>

                </div>
            </div>
            <VisibilityHandler isVisible={ APP_CONFIG.serverMode === "multi" }>
                <div
                    className={classes.marketingDiv}
                    style={{ textAlign: "center" }}
                >
                    <div>
                        <div style={{ textAlign: "left" }}>
                            <MarkdownRenderer
                                source={ language.userManagment.registerLink }
                                className={classes.marketingText}
                            />
                        </div>

                        <div className="flex justify-center full-width" >
                            <LinkButton
                                large
                                nonResponsive
                                path={RouteConfig.register.routes.main.path}
                                variant={"tertiary"}
                                tooltip={language.userManagment.registerBtnTooltip}
                                text={language.userManagment.register}
                                icon={<AppRegistration/>}
                                style={{ marginTop: "2rem", padding: "1.5rem", fontSize: "1.1rem" }}
                            />
                        </div>

                    </div>
                </div>
            </VisibilityHandler>
        </div>
    );
}

export default LoginForm;